<template>
  <v-container class="v3-reports-sidebar reports-sidebar-container">
    <v-row justify="center">
      <v-col cols="12" class="px-0 py-0">
        <!-- Selected Filters -->
        <reports-selected-filters
          :reset-selections="resetSelections"
          :class="smAndDown ? 'pa-3' : ''"
        />

        <v-divider v-show="!smAndDown" class="mt-3"></v-divider>

        <!-- All Filter Sections -->
        <div v-show="!smAndDown" class="filter-sidebar reports-filter-sidebar">
          <reports-all-filters />
        </div>
        <v-card v-show="smAndDown" class="pa-3 mb-3 reports-mobile-filters">
          <reports-all-filters />
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
  import ReportsAllFilters from './ReportsAllFilters.vue';
  import ReportsSelectedFilters from './ReportsSelectedFilters.vue';
  import { useReportsStore } from '../../store';
  import { useDisplay } from 'vuetify';
  import { computed, nextTick } from 'vue';
  import { useRouter } from 'vue-router';

  const reportsStore = useReportsStore();
  const { smAndDown } = useDisplay();
  const router = useRouter();

  const resetSelections = async () => {
    reportsStore.selectedSubjects = [];
    reportsStore.selectedFormats = [];
    reportsStore.selectedGrades = [];
    reportsStore.selectedVersions = [];
    reportsStore.setGradeRange(0, 12); // Reset grade range to K-12

    // Update route to /reports when all filters are cleared
    await nextTick();
    router.replace({ path: '/reports' });
  };
</script>

<style scoped lang="scss">
  // Use highly specific selectors to override production CSS conflicts
  .v3-reports-sidebar.reports-sidebar-container {
    // Increase specificity for filters header
    :deep(.filters-header) {
      font-weight: 500 !important;
    }

    // More specific selector for small selects
    :deep(.small-select) {
      max-width: 210px !important;
      padding-right: 12px !important;

      .v-field--appended {
        padding-inline-end: 0 !important;
      }

      .v-field {
        border-color: rgb(var(--v-theme-lighter-grey)) !important;
      }

      .v-field__outline {
        --v-field-border-width: 1px !important;
      }

      .v-input--density-compact {
        --v-input-control-height: 32px !important;
        --v-input-padding-top: 5px !important;
      }

      .v-input__control {
        height: 32px !important;
      }

      .v-field__input {
        min-height: 0 !important;
      }

      .v-field__append-inner .v-icon {
        transition: transform 0.2s ease !important;
      }

      .mdi-chevron-down {
        color: rgb(var(--v-theme-blue)) !important;
        opacity: 1 !important;
      }

      .v-text-field--box .v-input__slot,
      .v-text-field--outline .v-input__slot {
        min-height: auto !important;
        display: flex !important;
        align-items: center !important;
      }

      .v-field {
        min-height: 32px !important;
        font-size: 12px !important;
      }

      .mdi-menu-down {
        display: none !important;
      }
    }

    // More specific selector for expansion panels
    :deep(.reports-filter-panels) {
      background: transparent !important;
      box-shadow: none !important;
      contain: layout style !important;

      .v-expansion-panel {
        background: transparent !important;
        box-shadow: none !important;
        border: none !important;
        contain: content !important;

        .v-expansion-panel-title__icon {
          color: rgb(var(--v-theme-primary)) !important;
        }
      }

      .v-expansion-panel-title {
        min-height: unset !important;
        border: none !important;
        touch-action: manipulation !important;
      }

      .v-expansion-panel-title__overlay {
        display: none !important;
      }

      .v-expansion-panel-title--active {
        min-height: unset !important;
        border: none !important;
      }

      .v-expansion-panel-text__wrapper {
        padding: 0 !important;
      }

      .v-expansion-panel::after {
        border: none !important;
      }
    }

    // More specific selector for filter sections
    :deep(.reports-filter-section) {
      border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
      contain: content !important;
    }

    // More specific selector for filter content
    :deep(.reports-filter-content) {
      padding-bottom: 16px !important;
      contain: content !important;

      .v-checkbox {
        .v-label {
          font-size: 13px !important;
        }

        @media (max-width: 768px) {
          .v-selection-control {
            min-height: 40px !important;
            touch-action: manipulation !important;
          }
        }
      }
    }
  }

  // Additional specific selectors for mobile filters
  .reports-mobile-filters.v-card {
    padding: 12px !important;
    margin-bottom: 12px !important;
  }

  // Specific selector for the filter sidebar
  .filter-sidebar.reports-filter-sidebar {
    width: 100% !important;
    box-sizing: border-box !important;
  }

  // Override any global styles that might interfere
  .v3-reports-sidebar.reports-sidebar-container {
    * {
      box-sizing: border-box !important;
    }

    &.v-container {
      padding: 12px 12px 12px 0 !important;
    }

    .v-container {
      padding: 12px 12px 12px 0 !important;
    }

    .v-row {
      margin: 0 !important;
    }

    .v-col {
      padding: 0 !important;
    }
  }
</style>
